import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { LogOut, Plus, Pencil, Trash2, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { LogoMark } from '@/components/Ornaments';
import { PillButton } from '@/components/ui-bits';
import { api } from '@/lib/api';

const inputCls = 'w-full rounded-xl border border-input bg-beige/60 px-4 py-3 text-textDark placeholder:text-textDark/40 focus:outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 transition-colors';
const EMPTY = { slug_it: '', slug_en: '', title_it: '', title_en: '', excerpt_it: '', excerpt_en: '', content_it: '', content_en: '', cover_image: '', published: true };

export default function Admin() {
  const { t } = useTranslation();
  const { user, loading, logout } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, {} = new/edit
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState('it');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/blog').then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const openNew = () => { setForm(EMPTY); setEditing({}); setTab('it'); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p); setTab('it'); };
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.post_id) await api.put(`/admin/blog/${editing.post_id}`, form);
      else await api.post('/admin/blog', form);
      toast.success('OK');
      setEditing(null);
      load();
    } catch {
      toast.error(t('form.error'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`${t('admin.delete')}?`)) return;
    try { await api.delete(`/admin/blog/${p.post_id}`); load(); toast.success('OK'); }
    catch { toast.error(t('form.error')); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-beige"><span className="text-terracotta w-14 h-14 animate-pulse"><LogoMark className="w-full h-full" /></span></div>;
  }

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-beige px-6" data-testid="admin-login">
        <span className="text-terracotta w-16 h-16 mb-6"><LogoMark className="w-full h-full" /></span>
        <h1 className="font-serif text-4xl text-textDark mb-3">{t('admin.title')}</h1>
        <p className="text-textDark/60 mb-8 text-center max-w-sm">{t('admin.loginHint')}</p>
        <PillButton onClick={startLogin} testId="admin-google-login">{t('admin.login')}</PillButton>
        <Link to="/it" className="mt-8 text-terracotta font-semibold inline-flex items-center gap-2"><ArrowLeft size={16} /> Home</Link>
      </div>
    );
  }

  // Logged in but not authorized
  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-beige px-6 text-center" data-testid="admin-not-allowed">
        <span className="text-terracotta w-14 h-14 mb-5"><LogoMark className="w-full h-full" /></span>
        <p className="text-xl text-textDark max-w-md mb-6">{t('admin.notAllowed')}</p>
        <p className="text-textDark/50 mb-6">{user.email}</p>
        <PillButton variant="outline" onClick={logout} testId="admin-logout"><LogOut size={18} /> {t('admin.logout')}</PillButton>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-beige" data-testid="admin-dashboard">
      <div className="border-b border-terracotta/20 bg-beige/95 sticky top-0 z-20">
        <div className="max-w-[1100px] mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-terracotta w-9 h-9"><LogoMark className="w-full h-full" /></span>
            <span className="font-serif text-xl text-textDark">{t('admin.title')}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-textDark/50">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-textDark/20 px-4 py-2 text-sm font-semibold hover:border-terracotta transition-colors"><LogOut size={16} /> {t('admin.logout')}</button>
          </div>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-5 py-10">
        {!editing && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-serif text-3xl text-textDark">{t('admin.posts')} ({posts.length})</h2>
              <PillButton onClick={openNew} testId="admin-new-post"><Plus size={18} /> {t('admin.newPost')}</PillButton>
            </div>
            <div className="flex flex-col gap-3">
              {posts.map((p) => (
                <div key={p.post_id} data-testid={`admin-post-${p.post_id}`} className="bg-card rounded-2xl border border-terracotta/10 p-5 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-serif text-xl text-textDark truncate">{p.title_it}</p>
                    <p className="text-sm text-textDark/50 truncate">{p.title_en}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.post_id}`} className="w-10 h-10 rounded-full bg-salvia/20 text-salvia flex items-center justify-center hover:bg-salvia/30 transition-colors"><Pencil size={18} /></button>
                    <button onClick={() => remove(p)} data-testid={`admin-delete-${p.post_id}`} className="w-10 h-10 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center hover:bg-terracotta/25 transition-colors"><Trash2 size={18} /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {editing && (
          <form onSubmit={save} data-testid="admin-editor" className="bg-card rounded-3xl border border-terracotta/10 p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-3xl text-textDark">{editing.post_id ? t('admin.editPost') : t('admin.newPost')}</h2>
              <button type="button" onClick={() => setEditing(null)} className="text-textDark/50 hover:text-terracotta"><ArrowLeft size={22} /></button>
            </div>

            <label className="block text-sm font-semibold text-textDark/70 mb-1">{t('admin.cover')}</label>
            <input value={form.cover_image} onChange={upd('cover_image')} data-testid="admin-cover" className={`${inputCls} mb-5`} placeholder="https://…" />

            <div className="flex gap-2 mb-5">
              {['it', 'en'].map((l) => (
                <button type="button" key={l} onClick={() => setTab(l)} data-testid={`admin-tab-${l}`}
                  className={`rounded-full px-5 py-2 font-semibold transition-colors ${tab === l ? 'bg-terracotta text-beige' : 'bg-beige border border-textDark/15 text-textDark'}`}>
                  {t(`admin.${l}`)}
                </button>
              ))}
            </div>

            <div className="grid gap-4">
              <div>
                <label className="block text-sm font-semibold text-textDark/70 mb-1">{t('admin.slug')}</label>
                <input required value={form[`slug_${tab}`]} onChange={upd(`slug_${tab}`)} data-testid={`admin-slug-${tab}`} className={inputCls} placeholder="url-articolo" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-textDark/70 mb-1">{t('admin.articleTitle')}</label>
                <input required value={form[`title_${tab}`]} onChange={upd(`title_${tab}`)} data-testid={`admin-title-${tab}`} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-textDark/70 mb-1">{t('admin.excerpt')}</label>
                <textarea required rows={2} value={form[`excerpt_${tab}`]} onChange={upd(`excerpt_${tab}`)} data-testid={`admin-excerpt-${tab}`} className={`${inputCls} resize-none`} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-textDark/70 mb-1">{t('admin.content')}</label>
                <textarea required rows={12} value={form[`content_${tab}`]} onChange={upd(`content_${tab}`)} data-testid={`admin-content-${tab}`} className={`${inputCls} resize-none font-mono text-sm`} placeholder="Usa ## per i titoli, riga vuota tra i paragrafi" />
              </div>
            </div>

            <div className="mt-8 flex gap-3">
              <PillButton type="submit" disabled={saving} testId="admin-save">{saving ? t('common.sending') : t('admin.save')}</PillButton>
              <PillButton type="button" variant="outline" onClick={() => setEditing(null)} testId="admin-cancel">{t('admin.cancel')}</PillButton>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
